import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { FileInfo, CollectionModel } from '../types/models';
import { apiClient } from '../clients/APIClient';

export interface CollectionWithFiles extends CollectionModel {
  files: FileInfo[];
}

type CollectionState = {
  collections: CollectionWithFiles[];

  setCollections: (
    updater:
      | CollectionWithFiles[]
      | ((prev: CollectionWithFiles[]) => CollectionWithFiles[]),
  ) => void;

  fetchCollections: () => Promise<void>;
  removeFilesFromAllCollections: (documentIds: string[]) => void;
  addFilesToCollections: (args: { targetCollectionId?: string; documents: FileInfo[] }) => void;
};

export const useCollectionStore = create<CollectionState>()(
  devtools(
    (set, _get) => ({
      collections: [],

      setCollections: (updater) =>
        set(
          (state) => ({
            collections:
              typeof updater === 'function'
                ? (updater as (p: CollectionWithFiles[]) => CollectionWithFiles[])(state.collections)
                : updater,
          }),
          false,
          'collections/setCollections',
        ),

      fetchCollections: async () => {
        try {
          const collectionsData = await apiClient.collections.getCollections();

          if (collectionsData && typeof collectionsData === 'object' && 'error' in collectionsData) {
            console.error('Error fetching collections:', (collectionsData as any).error);
            set({ collections: [] }, false, 'collections/fetchCollections(error)');
            throw new Error('Failed to fetch collections');
          }

          let allDocuments: FileInfo[] = [];
          try {
            const allFiles = await apiClient.files.listFiles();
            allDocuments = Array.isArray(allFiles) ? allFiles : [];
          } catch (error) {
            console.error('Error loading all documents for All collection:', error);
            allDocuments = [];
          }

          const allCollection: CollectionWithFiles = {
            id: '-1',
            name: 'All',
            description: 'Contains all documents in your library',
            order: -1,
            files: allDocuments,
          };

          const collectionsWithFiles: CollectionWithFiles[] = await Promise.all(
            (collectionsData as CollectionModel[]).map(async (collection) => {
              try {
                const files = await apiClient.collections.getFilesInCollection(collection.id);
                return { ...collection, files: files && 'error' in files ? [] : (files as FileInfo[]) };
              } catch (error) {
                console.error(`Error loading files for collection ${collection.id}:`, error); // nosemgrep: javascript.lang.security.audit.unsafe-formatstring.unsafe-formatstring
                return { ...collection, files: [] };
              }
            }),
          );

          set({ collections: [allCollection, ...collectionsWithFiles] }, false, 'collections/fetchCollections(success)');
        } catch (err) {
          console.error('Error fetching collections:', err);
          set({ collections: [] }, false, 'collections/fetchCollections(fail)');
          throw err;
        }
      },

      removeFilesFromAllCollections: (documentIds: string[]) => {
        set(
          (state) => ({
            collections: state.collections.map((c) => ({
              ...c,
              files: c.files.filter((f) => !documentIds.includes(f.id)),
            })),
          }),
          false,
          'collections/removeFilesFromAllCollections',
        );
      },

      addFilesToCollections: ({ targetCollectionId, documents }) => {
        if (!documents?.length) return;

        set(
          (state) => ({
            collections: state.collections.map((collection) => {
              const shouldAdd =
                collection.id === '-1' || (targetCollectionId && collection.id === targetCollectionId);

              if (!shouldAdd) return collection;

              const existing = new Set(collection.files.map((f) => f.id));
              const uniqueNew = documents.filter((d) => !existing.has(d.id));

              return { ...collection, files: [...collection.files, ...uniqueNew] };
            }),
          }),
          false,
          'collections/addFilesToCollections',
        );
      },
    }),
    { name: 'useCollectionStore' },
  ),
);
